/**
 * Hachage du code PIN — une seule implémentation pour l'API (Node ≥ 20) et
 * la tablette (WebCrypto), parce que le même vérificateur doit être comparé
 * des deux côtés : côté serveur quand la tablette est en ligne, côté
 * appareil quand elle ne l'est pas (exigence N6).
 *
 * PBKDF2-SHA256 plutôt qu'argon2 : c'est le seul KDF que `crypto.subtle`
 * offre nativement, donc zéro dépendance et pas de wasm à charger hors
 * ligne. Sur un espace de 10 000 codes, aucun KDF ne rend le vérificateur
 * incassable pour qui le détient — le coût sert à ne jamais stocker le code
 * en clair (N1), pas à résister à une attaque hors ligne (cf. `PinVerifierSchema`).
 *
 * Format sérialisé : `pbkdf2-sha256$<itérations>$<sel b64>$<hash b64>`.
 */
const ALGO = 'pbkdf2-sha256';
const ITERATIONS = 100_000;
const SALT_BYTES = 16;
const HASH_BYTES = 32;

function toBase64(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin);
}

function fromBase64(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

async function derive(pin: string, salt: Uint8Array, iterations: number): Promise<Uint8Array> {
  const key = await crypto.subtle.importKey('raw', new TextEncoder().encode(pin), 'PBKDF2', false, [
    'deriveBits',
  ]);
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', hash: 'SHA-256', salt: salt as BufferSource, iterations },
    key,
    HASH_BYTES * 8,
  );
  return new Uint8Array(bits);
}

/** Comparaison à temps constant (exigence N2) — ne court-circuite jamais. */
export function constantTimeEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= (a[i] ?? 0) ^ (b[i] ?? 0);
  return diff === 0;
}

export async function hashPin(pin: string): Promise<string> {
  const salt = crypto.getRandomValues(new Uint8Array(SALT_BYTES));
  const hash = await derive(pin, salt, ITERATIONS);
  return [ALGO, String(ITERATIONS), toBase64(salt), toBase64(hash)].join('$');
}

/**
 * `false` sur un vérificateur malformé plutôt qu'une exception : côté
 * tablette, un cache corrompu doit se comporter comme un mauvais code, pas
 * comme un écran blanc.
 */
export async function verifyPin(pin: string, stored: string): Promise<boolean> {
  const parts = stored.split('$');
  if (parts.length !== 4 || parts[0] !== ALGO) return false;
  const iterations = Number(parts[1]);
  if (!Number.isInteger(iterations) || iterations < 1_000 || iterations > 10_000_000) return false;
  let salt: Uint8Array;
  let expected: Uint8Array;
  try {
    salt = fromBase64(parts[2] ?? '');
    expected = fromBase64(parts[3] ?? '');
  } catch {
    return false;
  }
  if (salt.length === 0 || expected.length !== HASH_BYTES) return false;
  const actual = await derive(pin, salt, iterations);
  return constantTimeEqual(actual, expected);
}
